import { useEffect, useMemo, useState } from "react";
import { generatePersonas, type GeneratePersonasResult } from "../domain/personas";
import type { DebateTopology } from "../domain/types";

const DEBOUNCE_MS = 400;
const MIN_QUERY_LEN = 4;

interface PreviewState {
  /** Whether the current `value` reflects the latest debounced input. */
  ready: boolean;
  /** Persona/seat preview synthesised from the debounced input. */
  value: GeneratePersonasResult | null;
}

/**
 * Debounced (400ms) preview of the council that would form for the current
 * (query, topology) tuple. Reads `domain/personas.generatePersonas` directly —
 * pure function, no I/O, no store mutation.
 *
 * Returns `null` value when the query is below `MIN_QUERY_LEN` so the UI can
 * render a ghost-seat placeholder.
 */
export function usePreviewedCouncil(
  query: string,
  topology: DebateTopology,
): PreviewState {
  const trimmed = query.trim();
  const [debounced, setDebounced] = useState(trimmed);

  useEffect(() => {
    if (trimmed.length < MIN_QUERY_LEN) {
      setDebounced("");
      return;
    }
    const timer = window.setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [trimmed]);

  const value = useMemo<GeneratePersonasResult | null>(() => {
    if (debounced.length < MIN_QUERY_LEN) return null;
    return generatePersonas(debounced, { topology });
  }, [debounced, topology]);

  return { ready: trimmed === debounced, value };
}
